(() => {
  'use strict';
  if (window.PBKeepsakePersonalization) return;
  const sent = new Set();
  const pending = new Set();
  const bound = new WeakSet();
  const values = new WeakMap();
  const fields = 'select,textarea,input[type="text"],input:not([type])';
  const choices = 'button[data-color],button[data-foil],button[data-orientation],button[data-placement],button[data-pb-kc-area],button[data-pb-kc-standard-area-v3],button.pb-kc__font-option';
  const selected = button => button.getAttribute('aria-pressed') === 'true' || button.getAttribute('aria-selected') === 'true' || button.classList.contains('is-selected');

  function start(root) {
    if (window.PBQA?.isInternal || sessionStorage.getItem('pb_qa_mode')) {
      root.dataset.pbPersonalizationTracking = 'internal-qa-suppressed';
      return;
    }
    const productId = root.id.match(/^PBKeepsakeConfigurator-(\d+)$/)?.[1];
    const format = root.dataset.mode === 'book' ? 'book_style' : root.dataset.mode === 'standard' ? 'standard' : '';
    if (!productId || !format || sent.has(productId) || pending.has(productId)) return;
    pending.add(productId);
    let attempts = 0;
    let requestedPrivacy = false;
    const stop = status => {
      pending.delete(productId);
      root.dataset.pbPersonalizationTracking = status;
    };
    const send = () => {
      if (!root.isConnected || sent.has(productId)) { stop('cancelled'); return; }
      const privacy = window.Shopify?.customerPrivacy;
      if (typeof privacy?.analyticsProcessingAllowed !== 'function') {
        if (!requestedPrivacy && typeof window.Shopify?.loadFeatures === 'function') {
          requestedPrivacy = true;
          window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], error => {
            if (error) stop('privacy-unavailable');
            else send();
          });
        } else stop('privacy-unavailable');
        return;
      }
      if (!privacy.analyticsProcessingAllowed()) { stop('consent-not-permitted'); return; }
      const payload = { send_to: 'G-BH383LQYPZ', product_id: productId, box_format: format };
      try {
        if (typeof window.gtag === 'function') window.gtag('event', 'personalization_start', payload);
        else if (Array.isArray(window.dataLayer) && document.querySelector('script[src*="googletagmanager.com/gtag/js"]')) {
          (function () { window.dataLayer.push(arguments); })('event', 'personalization_start', payload);
        } else {
          if (++attempts < 20) { setTimeout(send, 500); return; }
          stop('google-tag-unavailable');
          return;
        }
        try {
          if (typeof window.clarity === 'function') {
            window.clarity('set', 'personalization_started', 'true');
            window.clarity('set', 'keepsake_box_format', format);
            window.clarity('event', 'personalization_start');
          }
        } catch (_) {}
        sent.add(productId);
        stop('queued');
      } catch (_) { stop('google-tag-error'); }
    };
    send();
  }

  function bind(root) {
    if (!root || bound.has(root)) return;
    bound.add(root);
    root.querySelectorAll(fields).forEach(field => values.set(field, field.value));
    root.addEventListener('focusin', event => {
      if (event.target.matches(fields)) values.set(event.target, event.target.value);
    });
    const edit = event => {
      const field = event.target;
      if (!field.matches(fields) || field.disabled || field.readOnly) return;
      const previous = values.get(field);
      values.set(field, field.value);
      if (event.isTrusted && previous !== field.value) start(root);
    };
    root.addEventListener('input', edit);
    root.addEventListener('change', edit);
    root.addEventListener('click', event => {
      if (!event.isTrusted) return;
      const button = event.target.closest(choices);
      if (!button || !root.contains(button) || button.disabled || selected(button)) return;
      if (button.getAttribute('aria-pressed') !== 'false' && button.getAttribute('aria-selected') !== 'false') return;
      start(root);
    }, true);
  }
  window.PBKeepsakePersonalization = { bind };
})();

(() => {
  'use strict';
  if (window.PBKeepsakeRuntimeLoader) { window.PBKeepsakeRuntimeLoader.boot(); return; }
  const scripts = new Map();
  const styles = new Map();
  const bound = new WeakSet();
  function bindPreviewNavigation(root) {
    const preview = root.querySelector('.pb-kc__preview-wrap');
    const jump = root.querySelector('.pb-kc__preview-jump');
    const back = root.querySelector('.pb-kc__preview-back');
    if (!preview || !jump || !back || !('IntersectionObserver' in window)) return;
    let rootVisible = false;
    let previewVisible = true;
    let editor;
    const update = () => jump.classList.toggle('is-visible', rootVisible && !previewVisible);
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.target === root) rootVisible = entry.isIntersecting;
        if (entry.target === preview) previewVisible = entry.isIntersecting;
      });
      update();
    });
    observer.observe(root);
    observer.observe(preview);
    root.addEventListener('focusin', event => {
      if (event.target.matches('input:not([type="hidden"]),textarea,select,button') && !preview.contains(event.target)) editor = event.target;
    });
    back.addEventListener('click', event => {
      const target = editor?.isConnected ? editor : root.querySelector('select,textarea,input[type="text"],button');
      if (!target) return;
      event.preventDefault();
      target.scrollIntoView({ block: 'center', behavior: 'auto' });
      target.focus({ preventScroll: true });
    });
  }
  function loadStyle(src) {
    if (!src) return Promise.resolve();
    if (styles.has(src)) return styles.get(src);
    const promise = new Promise((resolve, reject) => {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = src;
      const fail = () => {
        clearTimeout(timer);
        link.onload = null;
        link.onerror = null;
        link.remove();
        styles.delete(src);
        reject(new Error('style-load'));
      };
      const timer = setTimeout(fail, 15000);
      link.onload = () => { clearTimeout(timer); resolve(); };
      link.onerror = fail;
      document.head.append(link);
    });
    styles.set(src, promise);
    return promise;
  }
  function loadScript(src) {
    if (scripts.has(src)) return scripts.get(src);
    const promise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = src;
      script.async = false;
      const fail = () => {
        clearTimeout(timer);
        script.onload = null;
        script.onerror = null;
        script.remove();
        scripts.delete(src);
        reject(new Error('script-load'));
      };
      const timer = setTimeout(fail, 15000);
      script.onload = () => { clearTimeout(timer); resolve(); };
      script.onerror = fail;
      document.head.append(script);
    });
    scripts.set(src, promise);
    return promise;
  }
  function init(host) {
    if (bound.has(host)) return;
    bound.add(host);
    const root = host.querySelector('[data-pb-kc]');
    const status = host.querySelector('[data-pb-runtime-status]');
    const button = host.querySelector('[data-pb-runtime-load]');
    const panel = host.querySelector('[data-pb-runtime-panel]');
    const slow = host.querySelector('[data-pb-runtime-slow]');
    const complete = host.querySelector('[data-pb-runtime-complete]');
    if (!root || !button || !status) return;
    root.inert = true;
    let loading = false;
    let observer;
    let visitorActive = false;
    let nearby = false;
    const activate = event => {
      if (!event.isTrusted) return;
      visitorActive = true;
      if (nearby && host.dataset.pbRuntimeState !== 'error') load();
    };
    const clearActivation = () => {
      ['pointerdown', 'keydown', 'touchstart', 'scroll'].forEach(type => window.removeEventListener(type, activate));
    };
    const load = async () => {
      if (!host.isConnected || loading || host.dataset.pbRuntimeState === 'ready') return;
      loading = true;
      const started = performance.now();
      host.dataset.pbRuntimeState = 'loading';
      root.setAttribute('aria-busy', 'true');
      status.textContent = host.dataset.loadingText;
      button.setAttribute('aria-disabled', 'true');
      button.textContent = host.dataset.loadingButtonText || host.dataset.loadingText;
      if (slow) slow.hidden = true;
      const slowTimer = setTimeout(() => { if (slow && host.isConnected) slow.hidden = false; }, 6000);
      try {
        const params = new URL(window.location.href).searchParams;
        const src = host.dataset.mode === 'standard' && params.get('pb_design') && params.get('pb_layout')
          ? host.dataset.marketplaceSrc : host.dataset.runtimeSrc;
        await loadStyle(host.dataset.runtimeStyle);
        await new Promise(resolve => {
          if (document.hidden || typeof requestAnimationFrame !== 'function') { resolve(); return; }
          const fallback = setTimeout(resolve, 200);
          requestAnimationFrame(() => requestAnimationFrame(() => { clearTimeout(fallback); resolve(); }));
        });
        if (!host.isConnected) return;
        await loadScript(src);
        if (!host.isConnected) return;
        if (root.dataset.pbKcReady !== 'true') {
          host.dispatchEvent(new CustomEvent('shopify:section:load', { bubbles: true, detail: { sectionId: root.dataset.sectionId } }));
        }
        if (root.dataset.pbKcReady !== 'true') throw new Error('runtime-incomplete');
        const focusWasOnButton = document.activeElement === button;
        root.removeAttribute('data-pb-runtime-pending');
        root.inert = false;
        root.setAttribute('aria-busy', 'false');
        status.hidden = true;
        button.hidden = true;
        if (panel) panel.hidden = true;
        if (complete) complete.textContent = host.dataset.loadedText || '';
        host.dataset.pbRuntimeState = 'ready';
        host.dataset.pbRuntimeDurationMs = String(Math.round(performance.now() - started));
        window.PBKeepsakePersonalization?.bind(root);
        bindPreviewNavigation(root);
        window.dispatchEvent(new Event('resize'));
        if (focusWasOnButton) {
          const field = root.querySelector('select:not([disabled]),textarea:not([disabled]),input[type="text"]:not([disabled]),button:not([disabled])');
          field?.focus({ preventScroll: true });
        }
        observer?.disconnect();
        clearActivation();
      } catch (_) {
        host.dataset.pbRuntimeState = 'error';
        root.setAttribute('aria-busy', 'false');
        status.textContent = host.dataset.errorText;
        button.textContent = host.dataset.retryText;
      } finally {
        clearTimeout(slowTimer);
        if (slow) slow.hidden = true;
        button.removeAttribute('aria-disabled');
        loading = false;
      }
    };
    button.addEventListener('click', load);
    const requestLoad = () => { if (host.dataset.pbRuntimeState !== 'error') load(); };
    host.addEventListener('focusin', requestLoad);
    host.addEventListener('pointerdown', requestLoad);
    ['pointerdown', 'keydown', 'touchstart', 'scroll'].forEach(type => window.addEventListener(type, activate, { passive: true }));
    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver(entries => {
        nearby = entries.some(entry => entry.isIntersecting);
        if (nearby && visitorActive && host.dataset.pbRuntimeState !== 'error') load();
      }, { rootMargin: '200px 0px' });
      observer.observe(host);
    }
  }
  function boot(scope = document) { scope.querySelectorAll('[data-pb-kc-loader]').forEach(init); }
  window.PBKeepsakeRuntimeLoader = { boot };
  boot();
  document.addEventListener('shopify:section:load', event => boot(event.target || document));
})();
